import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TablePagination,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { Edit, Delete, Description, ViewList } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RetentionTable = ({ retentions, onEdit, onDelete, page, onPageChange, pagination, canEdit, canDelete, sortBy, sortDir, onSortChange }) => {
  const navigate = useNavigate();

  const SortCell = ({ label, field, align, sx: extraSx }) => (
    <TableCell align={align} onClick={() => onSortChange(field)} sx={{ cursor: 'pointer', userSelect: 'none', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (!retentions || retentions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Description sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay tablas de retención registradas</p>
      </Paper>
    );
  }

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortCell label="Nombre" field="name" />
              <SortCell label="Código" field="code" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <SortCell label="Empresa" field="company.name" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <SortCell label="Área" field="area.name" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <SortCell label="Fecha" field="date" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Líneas</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Expedientes</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Documentos</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {retentions.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((retention) => (
              <TableRow
                key={retention.id}
                hover
                onClick={() => navigate(`/retentions/${retention.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Description color="primary" />
                    <strong>{retention.name}</strong>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip label={retention.code} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {retention.company ? (
                    <Box>
                      <div>{retention.company.name}</div>
                      <small style={{ color: 'gray' }}>{retention.company.short}</small>
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {retention.area ? (
                    <Chip label={retention.area.name} size="small" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(retention.date)}</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Tooltip title="Líneas de retención">
                    <Chip
                      icon={<ViewList />}
                      label={retention._count?.retentionLines || 0}
                      size="small"
                      color="secondary"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Chip label={retention._count?.proceedings ?? 0} size="small" />
                </TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Chip label={retention._count?.documents ?? 0} size="small" />
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); onEdit(retention); }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onDelete(retention.id); }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={(page || 1) - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit || 10}
          rowsPerPageOptions={[10]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default RetentionTable;
